import React, { useState, useEffect } from "react";
import {
  Container,
  Navbar,
  Form,
  InputGroup,
  Nav,
  Button,
  Badge,
  Dropdown,
} from "react-bootstrap";
import {
  Heart,
  Person,
  Cart,
  Search,
  List,
  House,
  Shop,
  Tools,
  InfoCircle,
  JournalText,
} from "react-bootstrap-icons";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import logo from "../Assets/logo.png";
import { motion } from "framer-motion";
import axios from "axios";
import "./Header.css";

const navItems = [
  { label: "Home", to: "/", icon: House },
  { label: "Shop", to: "/productPage", icon: Shop },
  { label: "Service", to: "/service-request", icon: Tools },
  { label: "About Us", to: "/aboutUs", icon: InfoCircle },
  { label: "Blog", to: "/blog", icon: JournalText },
];

const Header = () => {
  const { user, logout } = useAuth();
  const { cart = [] } = useCart();
  const { wishlist = [] } = useWishlist();
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        const response = await axios.get("https://madhusewingmachines.com/api/products");
        setAllProducts(response.data.products || []);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchAllProducts();
  }, []);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = allProducts.filter((product) => {
      const productName = (product.name || "").toLowerCase();
      const productBrand = (product.brand || "").toLowerCase();
      const productType = (product.type || "").toLowerCase();
      return (
        productName.includes(query) ||
        productBrand.includes(query) ||
        productType.includes(query)
      );
    });

    setSearchResults(filtered);
    setShowResults(query.length > 0);
  };

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      setShowResults(true);
    }
  };

  const handleResultClick = (productId) => {
    navigate(`/product/${productId}`);
    setShowResults(false);
    setExpanded(false);
  };

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  const closeMenu = () => setExpanded(false);

  const renderSearchResults = () =>
    showResults &&
    searchResults.length > 0 && (
      <motion.div
        className="pill-search-results"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {searchResults.map((product) => (
          <div
            key={product._id}
            className="pill-search-result-item"
            onClick={() => handleResultClick(product._id)}
          >
            <img
              src={
                product.images?.length > 0 && product.images[0]?.url
                  ? `https://madhusewingmachines.com/uploads/${product.images[0].url}`
                  : "/placeholder.jpg"
              }
              alt={product.name}
              style={{
                width: "48px",
                height: "48px",
                objectFit: "cover",
                borderRadius: "8px",
              }}
            />
            <div className="flex-grow-1">
              <div style={{ fontWeight: 500, color: "#333" }}>{product.name}</div>
              <div style={{ fontSize: "13px", color: "#777" }}>
                {product.brand} - {product.type}
              </div>
            </div>
            <div style={{ color: "#d4a017", fontWeight: 600 }}>₹{product.price}</div>
          </div>
        ))}
      </motion.div>
    );

  return (
    <Navbar expand="lg" fixed="top" className="pill-navbar" expanded={expanded}>
      <Container fluid="lg">
        <div className="pill-shell d-flex align-items-center">
          {/* Logo */}
          <Navbar.Brand as={Link} to="/" className="pill-logo me-2" onClick={closeMenu}>
            <motion.img
              src={logo}
              alt="Logo"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            />
          </Navbar.Brand>

          {/* Search (large screens) */}
          <Form
            className="pill-search d-none d-lg-block me-3"
            onSubmit={(e) => {
              e.preventDefault();
              handleSearchSubmit();
            }}
          >
            <InputGroup>
              <InputGroup.Text style={{ background: "transparent", border: "none" }}>
                <Search size={15} color="#888" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={handleSearch}
              />
            </InputGroup>
            {renderSearchResults()}
          </Form>

          {/* Hamburger */}
          <Navbar.Toggle
            aria-controls="main-nav"
            className="ms-auto border-0"
            onClick={() => setExpanded((prev) => !prev)}
          >
            <List size={28} />
          </Navbar.Toggle>

          {/* Collapsible content */}
          <Navbar.Collapse id="main-nav" className="justify-content-end">
            {/* Menu */}
            <Nav className="pill-nav align-items-lg-center mx-lg-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.to;
                return (
                  <Nav.Link
                    key={item.label}
                    as={Link}
                    to={item.to}
                    onClick={closeMenu}
                    className={isActive ? "active" : ""}
                  >
                    <Icon /> {item.label}
                  </Nav.Link>
                );
              })}
            </Nav>

            {/* Actions */}
            <div className="pill-actions d-flex align-items-center gap-2 ms-lg-2">
              {user ? (
                <Dropdown>
                  <Dropdown.Toggle
                    as={Button}
                    className="pill-btn pill-btn-outline"
                    id="profile-dropdown"
                  >
                    <Person size={16} /> Account
                  </Dropdown.Toggle>
                  <Dropdown.Menu align="end">
                    <Dropdown.Item as={Link} to="/profile" onClick={closeMenu}>
                      My Profile
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/OrderHistory" onClick={closeMenu}>
                      Order History
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/ServiceHistory" onClick={closeMenu}>
                      Service History
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item
                      onClick={() => {
                        closeMenu();
                        logout();
                      }}
                    >
                      Logout
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              ) : (
                <Button
                  className="pill-btn pill-btn-outline"
                  onClick={() => {
                    closeMenu();
                    navigate("/login");
                  }}
                >
                  <Person size={16} /> Login / Register
                </Button>
              )}

              {/* Wishlist */}
              <Link to="/wishlist" className="pill-wishlist px-2" onClick={closeMenu}>
                <Heart size={16} /> Wishlist
                {wishlist.length > 0 && (
                  <Badge bg="danger" pill className="ms-1">
                    {wishlist.length}
                  </Badge>
                )}
              </Link>

              {/* Cart */}
              <Button
                as={Link}
                to="/cart"
                className="pill-btn pill-btn-dark position-relative"
                onClick={closeMenu}
              >
                <Cart size={16} /> Cart
                {cart.length > 0 && (
                  <Badge
                    bg="danger"
                    pill
                    className="position-absolute top-0 start-100 translate-middle"
                  >
                    {cart.length}
                  </Badge>
                )}
              </Button>
            </div>

            {/* Search (small screens) */}
            <Form
              className="pill-search d-lg-none"
              onSubmit={(e) => {
                e.preventDefault();
                handleSearchSubmit();
              }}
            >
              <InputGroup>
                <InputGroup.Text style={{ background: "transparent", border: "none" }}>
                  <Search size={15} color="#888" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={handleSearch}
                />
              </InputGroup>
              {renderSearchResults()}
            </Form>
          </Navbar.Collapse>
        </div>
      </Container>
    </Navbar>
  );
};

export default Header;
